/* eslint-disable react/jsx-no-duplicate-props */
// Disabled for inputProps/InputProps - will be deprecated in MUI v7 in favor of slotProps: https://mui.com/material-ui/api/text-field/#text-field-prop-inputProps
import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  FontAwesomeIcon,
  Tooltip,
  IconButton,
} from '@schoolinks/ui-components';

import cn from 'classnames';
import { noop, debounce } from 'lodash';
import { oneOf, bool, string, func, shape } from 'prop-types';

import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';

import { debounceInterval } from 'constants/debounceInterval';
import { ensureString } from 'utils/string';

import s from './SearchInput.css';

const SearchInput = React.forwardRef(
  (
    {
      size,
      value,
      variant,
      usePlaceholderWidth,
      debounceAction,
      onChange,
      rootClass,
      isLoading,
      hasBorder,
      isDisabled,
      placeholder,
      searchInputClass,
      InputProps,
      label,
      floatingLabel,
      id,
    },
    ref,
  ) => {
    const searchInputRef = useRef();
    const [isActive, setIsActive] = useState(false);
    const [searchTerm, setSearchTerm] = useState(ensureString(value));
    const isSmallSize = size === 'small';
    const isMediumSize = size === 'medium';
    /**
     * Search inputs need persistent visual labels for accessibility. If a label
     * is passed in, it will float to the top when the input is focused. Use 'floatingLabel'
     * if you want to have shorter text when the input is focused and the label is floating.
     * isSmallSize condition is meant to limit the initial scope of this change
     */
    const useFloatingLabel = isSmallSize && (!!floatingLabel || !!label);
    const activeLabel = floatingLabel || label;

    const getIconSize = () => {
      if (useFloatingLabel) {
        return 'sm';
      }

      if (isSmallSize) {
        return 'lg';
      }

      if (isMediumSize) {
        return 'xl';
      }

      return 'lg';
    };

    const debouncedFunc = debounce(val => {
      debounceAction(val);
    }, debounceInterval);

    const debouncedAction = useCallback(debouncedFunc, [debouncedFunc]);

    const handleDebouncedOnChange = e => {
      const val = e.target.value;
      setSearchTerm(val);
      debouncedAction(val);
    };

    const onClear = () => {
      const emptyEvent = { target: { value: '' } };

      return debounceAction
        ? handleDebouncedOnChange(emptyEvent)
        : onChange(emptyEvent);
    };

    // Adjusts the width of the input based on the length of the placeholder text or label
    useEffect(() => {
      if (!usePlaceholderWidth) {
        return;
      }

      if (label) {
        searchInputRef?.current?.setAttribute(
          'size',
          searchInputRef?.current?.labels?.[0]?.textContent?.length,
        );
        return;
      }

      searchInputRef?.current?.setAttribute(
        'size',
        searchInputRef.current?.getAttribute('placeholder')?.length,
      );
    }, [usePlaceholderWidth, label]);

    return (
      <TextField
        label={isActive ? activeLabel : label}
        id={id}
        name="search"
        type="search"
        inputRef={searchInputRef}
        ref={ref}
        value={debounceAction ? searchTerm : value}
        size={size}
        color="secondary"
        variant="outlined"
        onChange={debounceAction ? handleDebouncedOnChange : onChange}
        disabled={isDisabled}
        placeholder={useFloatingLabel ? null : placeholder}
        onFocus={() => setIsActive(true)}
        onBlur={e => {
          if (!e?.target?.value) {
            setIsActive(false);
          }
        }}
        className={cn(rootClass, { [s.fullWidth]: !usePlaceholderWidth })}
        autoComplete="new-password"
        inputProps={{
          autocomplete: 'new-password',
          form: {
            autocomplete: 'off',
          },
        }}
        InputLabelProps={{
          shrink: useFloatingLabel && (!!value || isActive),
          classes: {
            root: useFloatingLabel && !isActive && s.floatingLabel,
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {isLoading ? (
                <CircularProgress
                  thickness={isSmallSize ? 8 : 6}
                  size={isSmallSize ? 24 : 32}
                />
              ) : (
                <FontAwesomeIcon
                  className={cn({
                    [s.searchIconSmall]: isSmallSize,
                    [s.searchIconMedium]: isMediumSize,
                  })}
                  icon={faMagnifyingGlass}
                  size={getIconSize()}
                />
              )}
            </InputAdornment>
          ),
          endAdornment:
            value || searchTerm ? (
              <Tooltip placement="top" title="Clear search">
                <IconButton aria-label="Clear search" onClick={onClear}>
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              </Tooltip>
            ) : null,
          className: cn(s.searchInput, searchInputClass, {
            [s.smallPadding]: isSmallSize,
            [s.pill]: variant === 'pill',
            [s.disabled]: isDisabled,
            [s.noBorder]: !hasBorder,
            [s.loading]: isLoading,
            [s.active]: isActive,
            [s.fullWidth]: !usePlaceholderWidth,
          }),
          ...InputProps,
        }}
      />
    );
  },
);

SearchInput.propTypes = {
  variant: oneOf(['rectangle', 'pill']),
  hasBorder: bool,
  value: string.isRequired,
  placeholder: string,
  size: oneOf(['small', 'medium']),
  onChange: func,
  isDisabled: bool,
  isLoading: bool,
  searchInputClass: string,
  rootClass: string,
  // If true, input element will shrink to width of placeholder text
  usePlaceholderWidth: bool,
  InputProps: shape({}),
  debounceAction: func,
  label: string,
  floatingLabel: string,
  id: string,
};

SearchInput.defaultProps = {
  variant: 'rectangle',
  hasBorder: true,
  placeholder: 'Search',
  size: 'medium',
  onChange: noop,
  isDisabled: false,
  isLoading: false,
  searchInputClass: '',
  rootClass: '',
  usePlaceholderWidth: false,
  InputProps: {},
  debounceAction: undefined,
  label: '',
  floatingLabel: '',
  id: 'search',
};

export default SearchInput;
